/* eslint-disable max-lines-per-function */
import React from "react";
import { ApolloProvider as ApolloHookProvider } from "@apollo/client";
import { Grid } from "@mui/material";
import { apolloClient } from "data/graphql";
import { Viewer } from "components/auth/types";
import { QueryInterestProfile_interest as InterestType } from "types/gql-types";
import { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import LogCallForm from "./log-call-form";
import MessageForm from "./message-form";
import NoteForm from "./note-form";
import ChangeStatusForm, { ChangeStatusStateValues } from "./change-status-form";
import { ActionKey, tabActions, tabsData } from "./helpers";
import { StateValues } from "./use-batch-actions-controller";

type Props = {
  action: Action | undefined;
  interest: InterestType;
  interestListID: string;
  viewer: Viewer;
  errorMsgs: {
    sendMessage: { form: string; email: string; sms: string; letter: string };
    logCall: string;
    addNote: string;
    changeStatus: string;
  };
  fieldErrors: {
    sendMessage: { emailSubject: string; smsUseCase: string };
    logCall: { outcome: string };
    addNote: { note: string };
    changeStatus: { status: string; reason: string };
  };
  changeStatusState: ChangeStatusStateValues;
  onSwitchChange: (values: StateValues, key: ActionKey) => void;
}

const ActionsViews: React.FC<Props> = ({
  action,
  interest,
  interestListID,
  viewer,
  errorMsgs,
  fieldErrors,
  changeStatusState,
  onSwitchChange,
}) => {
  const views: Record<ActionKey, JSX.Element> = {
    logCall: (
      <LogCallForm
        phones={[{ type: interest.householdMember0PrimaryPhoneType || "Phone", number: interest.householdMember0PrimaryPhone || "" }]}
        onChange={(values) => onSwitchChange(values, "logCall")}
        isShowing={action === Action.LogCall}
        fieldErrors={fieldErrors.logCall}
      />
    ),
    sendMessage: (
      <MessageForm
        interest={interest}
        interestListID={interestListID}
        viewer={viewer}
        onChange={(values) => onSwitchChange(values, "sendMessage")}
        isShowing={action === Action.SendMessage}
        errorMessages={errorMsgs.sendMessage}
        fieldErrors={fieldErrors.sendMessage}
      />
    ),
    addNote: (
      <NoteForm
        isLoading={false}
        onChange={(values) => onSwitchChange(values, "addNote")}
        isShowing={action === Action.AddNote}
        fieldErrors={fieldErrors.addNote}
      />
    ),
    changeStatus: (
      <ChangeStatusForm
        interest={interest}
        onChange={(values) => onSwitchChange(values, "changeStatus")}
        isShowing={action === Action.ChangeStatus}
        state={changeStatusState}
        fieldErrors={fieldErrors.changeStatus}
      />
    ),
  };

  return (
    <ApolloHookProvider client={apolloClient}>
      {tabsData.map(({ key }) => (
        <Grid key={key} size={{ xs: 12 }} style={{ display: action === tabActions[key] ? "block" : "none" }}>
          {views[key]}
        </Grid>
      ))}
    </ApolloHookProvider>
  );
};

export default ActionsViews;
